/**
 * Owner: Masa Bayu
 * File: write-dropcap.ts
 * Date:Time: 2026-10-14 03:58
 * Description: L3.dropcap writePageDropCapIndex · tip TipDropCapIndex · no canvas.
 */

import {
  detectNativeDropCapPairs,
  type NativeDropCapPair,
} from './dropcap.js';
import { groupLinesIntoParagraphs } from './group.js';
import type {
  LayoutWritePageInput,
  SpsuTextElement,
  SpsuTextLine,
  TipDropCapIndex,
  TipDropCapPair,
} from './types.js';

/** Tip space-width estimate · ~0.25em · not live canvas. */
const SPACE_EM = 0.25;
/** Face pad factor vs space · Q3 fontSize-only. */
const PAD_FACTOR = 0.35;
const PAD_MIN_PT = 0.75;

function asLine(el: SpsuTextElement): SpsuTextLine {
  return {
    id: el.id,
    content: el.content,
    bbox: el.bbox,
    style: {
      fontSize: el.style.fontSize,
      ...(el.style.fontFamily !== undefined
        ? { fontFamily: el.style.fontFamily }
        : {}),
      ...(el.style.textAlign !== undefined
        ? { textAlign: el.style.textAlign }
        : {}),
    },
    ...(el.role !== undefined ? { role: el.role } : {}),
  };
}

/**
 * Pad beside letter from tip fontSize only (Q3).
 * Mirrors Face `0.35 × space` without measureText.
 */
export function wrapPadPt(hostFontSize: number): number {
  const spaceEst = Math.max(0, hostFontSize) * SPACE_EM;
  return Math.max(PAD_FACTOR * spaceEst, PAD_MIN_PT);
}

/**
 * Port of Face estimateColumnWidth · tip bbox only · no remasure.
 */
export function estimateDropCapColumnWidth(
  texts: readonly SpsuTextLine[],
  host: SpsuTextLine,
  columnLeft: number,
): number {
  let right = host.bbox.x + host.bbox.width;
  const bodySize = host.style.fontSize;
  for (const t of texts) {
    if (Math.abs(t.style.fontSize - bodySize) > 1.5) continue;
    right = Math.max(right, t.bbox.x + t.bbox.width);
  }
  return Math.max(host.bbox.width, right - columnLeft);
}

function flowIdsForPair(
  pair: NativeDropCapPair,
  textsById: ReadonlyMap<string, SpsuTextLine>,
  groups: readonly SpsuTextLine[][],
): readonly string[] {
  const group = groups.find((g) => g.some((p) => p.id === pair.letterId));
  const candidateIds = new Set<string>();
  if (group) {
    for (const p of group) {
      if (p.id !== pair.letterId) candidateIds.add(p.id);
    }
  } else {
    candidateIds.add(pair.hostId);
    for (const id of pair.absorbIds) candidateIds.add(id);
  }

  return [...candidateIds]
    .map((id) => textsById.get(id))
    .filter((el): el is SpsuTextLine => el != null)
    .sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x)
    .map((el) => el.id);
}

function pairToTip(
  pair: NativeDropCapPair,
  texts: readonly SpsuTextLine[],
  textsById: ReadonlyMap<string, SpsuTextLine>,
  groups: readonly SpsuTextLine[][],
): TipDropCapPair {
  const { letter, host } = pair;
  const columnLeft = Math.min(letter.bbox.x, host.bbox.x);
  const top = Math.min(letter.bbox.y, host.bbox.y);
  const columnWidth = estimateDropCapColumnWidth(texts, host, columnLeft);
  const bboxInset = Math.max(0, host.bbox.x - columnLeft);
  const pad = wrapPadPt(host.style.fontSize);
  // Tip: letter.bbox.width stands in for Face glyphInset (no canvas).
  const wrapInset = Math.max(bboxInset, letter.bbox.width + pad);
  const wrapLineCount = 1 + pair.absorbIds.length;
  const flowIds = flowIdsForPair(pair, textsById, groups);

  return {
    letterId: pair.letterId,
    hostId: pair.hostId,
    absorbIds: [...pair.absorbIds],
    flowIds,
    columnLeft,
    columnWidth,
    top,
    wrapInset,
    wrapLineCount,
  };
}

/**
 * L3.dropcap tip writer · one page.
 * Detect once → TipDropCapIndex · empty pairs = fail-closed (no invent).
 * Does not mutate elements · does not call justifyDecision.
 * Q6 OCR merge path OUT of scope.
 */
export function writePageDropCapIndex(
  input: LayoutWritePageInput,
): TipDropCapIndex {
  const { elements } = input;
  if (elements.length < 2) {
    return { version: 1, pairs: [] };
  }

  const lines = elements.map(asLine);
  const detected = detectNativeDropCapPairs(lines);
  if (detected.length === 0) {
    return { version: 1, pairs: [] };
  }

  const textsById = new Map(lines.map((l) => [l.id, l] as const));
  const groups = groupLinesIntoParagraphs(lines);
  const pairs = detected.map((p) => pairToTip(p, lines, textsById, groups));
  return { version: 1, pairs };
}
